import React from "react";
import { TowerControl, Cable, Cpu, Wallet, Network, ShieldCheck, ArrowUpRight } from "lucide-react";

const NETWORK_IMG =
  "https://static.prod-images.emergentagent.com/jobs/0d225e69-bbdf-46e2-9a5f-5e718f2e6f47/images/9bbaa2c0f80be12362dfae6b3eb82411970f000be6586e839eef825219148bb7.png";

const pillars = [
  {
    icon: TowerControl,
    title: "Macro towers",
    body: "Long-lease ground assets with carriers. The original yield infrastructure.",
  },
  {
    icon: Cable,
    title: "Dark & lit fiber",
    body: "The capillary system of the internet — quietly compounding.",
  },
  {
    icon: Cpu,
    title: "Edge compute",
    body: "Micro-DCs at the base of every tower. The next-decade buildout.",
  },
];

const Vision = () => {
  return (
    <section
      id="vision"
      data-testid="vision-section"
      className="relative overflow-hidden bg-black py-28 md:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {/* Section header */}
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p
              data-testid="vision-eyebrow"
              className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.28em] text-orange-400"
            >
              <Network className="h-3.5 w-3.5" /> The thesis
            </p>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl">
              The grid is the most boring,{" "}
              <span className="text-orange-400">most lucrative</span> asset
              class you never owned.
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-neutral-400">
            For decades, towers, fiber and edge compute have sat behind REITs,
            pension funds and sovereign wealth. InfraVest is building the
            on-ramp for everyone else.
          </p>
        </div>

        {/* Bento grid */}
        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-12">
          {/* Big visual card */}
          <div
            data-testid="vision-bento-network"
            className="relative col-span-1 overflow-hidden rounded-3xl border border-white/10 bg-neutral-950 md:col-span-7 md:row-span-2"
          >
            <img
              src={NETWORK_IMG}
              alt="Abstract glowing infrastructure network"
              className="h-full w-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8 md:p-10">
              <p className="text-xs uppercase tracking-[0.22em] text-orange-300">
                Network · 2026
              </p>
              <h3 className="mt-3 max-w-lg font-display text-2xl font-semibold text-white sm:text-3xl">
                One graph. Every tower, every strand, every node.
              </h3>
              <p className="mt-3 max-w-md text-sm text-neutral-300">
                The InfraVest atlas maps the physical internet — so capital can
                finally see what it owns.
              </p>
            </div>
            <div className="absolute right-6 top-6 grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-black/40 backdrop-blur-md">
              <ArrowUpRight className="h-4 w-4 text-white" />
            </div>
          </div>

          {/* Pillars */}
          {pillars.map((p, i) => (
            <div
              key={i}
              data-testid={`vision-pillar-${i}`}
              className="group relative col-span-1 overflow-hidden rounded-3xl border border-white/10 bg-neutral-950 p-7 transition-colors hover:border-orange-500/30 md:col-span-5"
            >
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl border border-orange-500/30 bg-orange-500/10 text-orange-400 transition-transform duration-300 group-hover:scale-105">
                  <p.icon className="h-5 w-5" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                  0{i + 1} / 03
                </div>
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold text-white">
                {p.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                {p.body}
              </p>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 md:grid-cols-4">
          {[
            { v: "98%", l: "Carrier-lease occupancy" },
            { v: "20y+", l: "Avg. tower lease duration" },
            { v: "6×", l: "Edge sites by 2030" },
            { v: "0", l: "Public retail access today" },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`vision-stat-${i}`}
              className="bg-neutral-950 p-7"
            >
              <div className="font-display text-4xl font-semibold text-white">
                {s.v}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.2em] text-neutral-500">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Vision;
